<template>
  <v-sheet class="fs-scheduler">
    <v-toolbar flat>
      <v-toolbar-title class="mr-4 font-weight-bold">
        Field Service Scheduling
      </v-toolbar-title>

      <v-btn outlined class="mr-2" @click="setToday">Today</v-btn>
      <v-btn icon @click="prev"><v-icon>mdi-chevron-left</v-icon></v-btn>
      <v-btn icon @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>

      <v-toolbar-title class="ml-4">{{ title }}</v-toolbar-title>
      <v-spacer />

      <v-select v-model="viewType" :items="viewOptions" dense outlined hide-details style="max-width: 160px"
        @change="onViewChange" />
    </v-toolbar>

    <v-calendar v-if="viewType === 'month'" ref="cal" v-model="value" :type="viewType" :events="calendarEvents"
      :event-color="(e) => e.color" @change="updateTitle" />

    <div v-else-if="viewType === 'day'" class="lanes-wrapper">
      <div ref="timeHeader" class="time-header" @scroll.passive="onHeaderScroll">
        <div class="time-header-inner" :style="{ width: timelineTotalWidthPx }">
          <div v-for="h in hours" :key="h" class="time-tick" :style="{ left: hourToPx(h) }">
            {{ pad2(h) }}:00
          </div>

          <div v-for="m in gridMinutes" :key="`g-${m}`" class="grid-line" :style="{ left: minutesToPx(m) }" />
        </div>
      </div>

      <div ref="rows" class="rows" @scroll.passive="onRowsScroll">
        <div class="lane">
          <div class="lane-title">
            <v-chip small class="lane-chip" :style="laneChipStyle(unassignedLane)">
              <v-avatar left size="18" :color="unassignedLane.color" class="white--text">
                {{ unassignedLane.avatarText }}
              </v-avatar>
              <span class="lane-chip__name">{{ unassignedLane.name }}</span>
              <span class="lane-chip__count">{{ unassignedLane.count }}</span>
              <span class="status-dot" :style="{ background: statusColor(unassignedLane.status) }" />
            </v-chip>
          </div>

          <div class="row-inner" :class="{ 'is-drop-target': dropTargetKey === TECH_UNASSIGNED_KEY }"
            :style="{ width: timelineTotalWidthPx }"
            @dragenter.prevent="onDragEnterDropZone(TECH_UNASSIGNED_KEY, $event)" @dragover.prevent="onDragOverDropZone"
            @drop.stop.prevent="handleDropTech(null, $event)">
            <div v-if="dropTargetKey === TECH_UNASSIGNED_KEY" class="drop-label"
              :style="{ background: hexToRgba(unassignedLane.color, 0.9) }">
              <span class="drop-label__avatar" :style="{ background: unassignedLane.color }">
                {{ unassignedLane.avatarText }}
              </span>
              {{ unassignedLane.name }}
            </div>

            <div v-for="ev in unassignedEventsForDay" :key="ev.id" class="event-block" :class="eventClasses(ev)"
              :style="eventStyleDay(ev)" draggable="true" @dragstart="onDragStart(ev, $event)" @dragend="clearDrag"
              @click.stop="openEventDialog(ev)">
              <div class="event-title">
                <span v-if="isTinyEvent(ev)" class="event-num">#{{ ev.callNum }}</span>
                <span v-else>{{ ev.title }}</span>
              </div>
              <div v-if="!isTinyEvent(ev)" class="event-sub">
                {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
              </div>
            </div>
          </div>
        </div>

        <div v-for="t in techList" :key="t.id" class="lane">
          <div class="lane-title">
            <v-chip small class="lane-chip" :style="laneChipStyle(techLane(t))">
              <v-avatar left size="18" :color="t.color" class="white--text">
                {{ t.avatarText }}
              </v-avatar>
              <span class="lane-chip__name">{{ t.name }}</span>
              <span class="lane-chip__count">{{ techLane(t).count }}</span>
              <span class="status-dot" :style="{ background: statusColor(techLane(t).status) }" />
            </v-chip>
          </div>

          <div class="row-inner" :class="{ 'is-drop-target': dropTargetKey === t.id }"
            :style="{ width: timelineTotalWidthPx }" @dragenter.prevent="onDragEnterDropZone(t.id, $event)"
            @dragover.prevent="onDragOverDropZone" @drop.stop.prevent="handleDropTech(t.id, $event)">
            <div v-if="dropTargetKey === t.id" class="drop-label" :style="{ background: hexToRgba(t.color, 0.9) }">
              <span class="drop-label__avatar" :style="{ background: t.color }">
                {{ t.avatarText }}
              </span>
              {{ t.name }}
            </div>

            <div v-for="ev in eventsByTechForDay(t.id)" :key="ev.id" class="event-block" :class="eventClasses(ev)"
              :style="eventStyleDay(ev)" draggable="true" @dragstart="onDragStart(ev, $event)" @dragend="clearDrag"
              @click.stop="openEventDialog(ev)">
              <div class="event-title">
                <span v-if="isTinyEvent(ev)" class="event-num">#{{ ev.callNum }}</span>
                <span v-else>{{ ev.title }}</span>
              </div>
              <div v-if="!isTinyEvent(ev)" class="event-sub">
                {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="week-wrapper">
      <div class="tech-panel">
        <div class="tech-panel__title">Technicians</div>

        <div class="tech-item" :class="{ 'is-drop-target': dropTargetKey === TECH_UNASSIGNED_KEY }"
          @dragenter.prevent="onDragEnterDropZone(TECH_UNASSIGNED_KEY, $event)" @dragover.prevent="onDragOverDropZone"
          @drop.stop.prevent="handleDropTech(null, $event)">
          <v-chip small class="lane-chip" :style="laneChipStyle(unassignedLane)">
            <v-avatar left size="18" :color="unassignedLane.color" class="white--text">
              {{ unassignedLane.avatarText }}
            </v-avatar>
            <span class="lane-chip__name">{{ unassignedLane.name }}</span>
            <span class="lane-chip__count">{{ unassignedLane.count }}</span>
            <span class="status-dot" :style="{ background: statusColor(unassignedLane.status) }" />
          </v-chip>
        </div>

        <div class="unassigned-list" v-if="unassignedEventsForWeek.length">
          <div v-for="ev in unassignedEventsForWeek" :key="ev.id" class="unassigned-item" draggable="true"
            @dragstart="onDragStart(ev, $event)" @dragend="clearDrag" @click.stop="openEventDialog(ev)">
            <div class="unassigned-item__title">{{ ev.title }}</div>
            <div class="unassigned-item__sub">
              {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
            </div>
          </div>
        </div>

        <div v-for="t in techList" :key="t.id" class="tech-item" :class="{ 'is-drop-target': dropTargetKey === t.id }"
          @dragenter.prevent="onDragEnterDropZone(t.id, $event)" @dragover.prevent="onDragOverDropZone"
          @drop.stop.prevent="handleDropTech(t.id, $event)">
          <v-chip small class="lane-chip" :style="laneChipStyle(techLane(t))"
            :class="{ 'is-selected': selectedTechIds.has(t.id) }" @click="toggleTechFilter(t.id)">
            <v-avatar left size="18" :color="t.color" class="white--text">
              {{ t.avatarText }}
            </v-avatar>
            <span class="lane-chip__name">{{ t.name }}</span>
            <span class="lane-chip__count">{{ techLane(t).count }}</span>
            <span class="status-dot" :style="{ background: statusColor(techLane(t).status) }" />
          </v-chip>
        </div>

        <div class="tech-panel__hint">
          Drag call → drop onto tech to assign / drop Unassigned to unassign.
        </div>
      </div>

      <div class="week-grid">
        <div class="week-grid__header">
          <div class="week-grid__corner"></div>
          <div class="week-grid__days">
            <div v-for="d in weekDays" :key="d.key" class="day-col-head">
              <div class="dow">{{ d.dow }}</div>
              <div class="dom">{{ d.dom }}</div>
            </div>
          </div>
        </div>

        <div ref="weekScroll" class="week-grid__body" @scroll.passive="onWeekScroll">
          <div class="time-col" :style="{ height: weekBodyHeightPx }">
            <div v-for="h in hours" :key="`wh-${h}`" class="time-label" :style="{ top: weekMinToTop(h * 60) }">
              {{ pad2(h) }}:00
            </div>
          </div>

          <div class="days-area" :style="{ height: weekBodyHeightPx }">
            <div v-for="(d, dayIdx) in weekDays" :key="`col-${d.key}`" class="day-col"
              :class="{ 'is-drop-target': dropTargetKey === `week-day-${dayIdx}` }" :data-day-index="dayIdx"
              @dragenter.prevent="onDragEnterWeekDay(dayIdx, $event)"
              @dragover.prevent="onDragOverWeekDay(dayIdx, $event)"
              @drop.stop.prevent="handleDropWeekDay(d, dayIdx, $event)">
              <div v-for="h in hours" :key="`l-${d.key}-${h}`" class="hour-line"
                :style="{ top: weekMinToTop(h * 60) }" />

              <div v-for="ev in eventsForWeekDay(d.startMs, d.endMs)" :key="ev.id" class="week-event"
                :style="eventStyleWeek(ev)" draggable="true" @click.stop="openEventDialog(ev)"
                @dragstart="onDragStart(ev, $event)" @dragend="clearDrag">
                <div class="week-event__title">
                  Call {{ ev.callNum }} - {{ ev.techId || "Unassigned" }}
                </div>
                <div class="week-event__sub">
                  {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="toast-stack">
      <transition-group name="toast">
        <div v-for="t in toasts" :key="t.id" class="toast-item" :class="`toast--${t.color}`">
          <span class="toast-item__text">{{ t.text }}</span>
          <button class="toast-item__close" @click="removeToast(t.id)">✕</button>
        </div>
      </transition-group>
    </div>

    <v-dialog v-model="dialog" max-width="500">
      <v-card v-if="selectedEvent">
        <v-card-title>
          Call {{ selectedEvent.callNum }}
        </v-card-title>

        <v-card-text>
          <div>
            <b>Technician:</b>
            {{ techNameById(selectedEvent.techId) }}
          </div>

          <div>
            <b>Start:</b>
            {{ formatHM(selectedEvent.startMs) }}
          </div>

          <div>
            <b>End:</b>
            {{ formatHM(selectedEvent.endMs) }}
          </div>

          <div>
            <b>Job:</b>
            {{ selectedEvent.jobNum }}
          </div>

          <div>
            <b>Seq:</b>
            {{ selectedEvent.seqNum }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import { mapGetters } from "vuex";

const TECH_PALETTE = [
  "#031716",
  "#032F30",
  "#0A7075",
  "#0C969C",
  "#6BA3BE",
  "#274D60",
  "#0B525B",
  "#144552",
  "#1B6B6F",
  "#2CA6A4",
  "#3D8B8A",
  "#5CB8B2",
];

const COMPANY = "EPIC03";
const DND_MIME = "text/plain";
const DEFAULT_UNASSIGNED_COLOR = "#757575";
const TECH_UNASSIGNED_KEY = "unassigned";

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeAssignee(v) {
  const s = v == null ? "" : String(v).trim();
  if (!s || s === "0") return null;
  return s;
}

function toSecondsFromMidnight(date) {
  return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

function buildEventKey({ callNum, techId, seqNum, jobNum }) {
  return [
    String(callNum ?? ""),
    String(techId ?? TECH_UNASSIGNED_KEY),
    String(seqNum ?? 0),
    String(jobNum ?? ""),
  ].join("|");
}

export default {
  name: "FsScheduling",

  data() {
    return {
      TECH_UNASSIGNED_KEY,
      value: formatDateLocal(new Date()),
      viewType: "week",
      viewOptions: [
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
      ],
      title: "",

      dayStartHour: 0,
      dayEndHour: 24,
      pxPerMin: 2,
      laneGutterPx: 240,
      tinyEventWidthPx: 90,

      weekPxPerMin: 1,
      weekHeaderHeight: 56,

      dragEventId: null,
      dropTargetKey: null,

      syncFromHeader: false,
      syncFromRows: false,

      selectedTechIds: new Set(),
      toasts: [],

      pendingEventOverrides: {},

      dialog: false,
      selectedEvent: null,
    };
  },

  computed: {
    ...mapGetters("Data", ["getOpenCall", "technianList"]),

    techList() {
      return (this.technianList || []).map((t) => {
        const id = String(t.EmpBasic_EmpID);
        return {
          id,
          name: t.EmpBasic_Name,
          avatarText: this.initials(t.EmpBasic_Name),
          color: this.techColor(id),
        };
      });
    },

    empIdSet() {
      return new Set(this.techList.map((t) => t.id));
    },

    calls() {
      return (this.getOpenCall || []).map((el, idx) => {
        const callNum = Number(el.FSCallhd_CallNum);
        const schedDate = el.FSCallhd_SchedDate;
        const schedTime = Number(el.FSCallhd_RequestTime ?? 0);
        const endTime = Number(el.FSCallhd_SchedTime ?? 0);

        const startMs = this.combineDateAndSecondsToMs(schedDate, schedTime);
        const endMs = this.combineDateAndSecondsToMs(schedDate, endTime);

        const durationHours = Number(el.FSCallhd_Duration ?? 1);

        const baseTechId = normalizeAssignee(el.FsTech_EmpID);
        const seqNum = Number(el.FsTech_SeqNum ?? 1);
        const jobNum = el.JobHead_JobNum;

        const baseKey = buildEventKey({
          callNum,
          techId: baseTechId,
          seqNum,
          jobNum,
        });

        const override = this.pendingEventOverrides[baseKey] || {};

        const finalStartMs = override.startMs ?? startMs;
        const finalEndMs = override.endMs ?? endMs;

        const finalTechId = Object.prototype.hasOwnProperty.call(override, "techId")
          ? override.techId
          : baseTechId;

        const finalKey = buildEventKey({
          callNum,
          techId: finalTechId,
          seqNum,
          jobNum,
        });

        return {
          rowIndex: idx,
          id: finalKey,
          originalId: baseKey,
          callNum,
          title: `Call ${callNum} - ${finalTechId || "Unassigned"}`,
          startMs: finalStartMs,
          endMs: finalEndMs,
          techId: finalTechId,
          techName: normalizeAssignee(el.FsTech_Name),
          color: finalTechId ? this.techColor(finalTechId) : DEFAULT_UNASSIGNED_COLOR,
          seqNum,
          jobNum,
          durationHours,
        };
      });
    },

    totalMinutes() {
      return (this.dayEndHour - this.dayStartHour) * 60;
    },

    timelineWidthPx() {
      return this.totalMinutes * this.pxPerMin;
    },

    timelineTotalWidthPx() {
      return `${this.laneGutterPx + this.timelineWidthPx}px`;
    },

    hours() {
      const result = [];
      for (let h = this.dayStartHour; h < this.dayEndHour; h += 1) result.push(h);
      return result;
    },

    gridMinutes() {
      const result = [];
      for (let m = 0; m <= this.totalMinutes; m += 30) result.push(m);
      return result;
    },

    calendarEvents() {
      return this.calls.map((c) => ({
        name: `${c.title}`,
        start: new Date(c.startMs),
        end: new Date(c.endMs),
        color: c.color || "#0C969C",
      }));
    },

    weekRangeMs() {
      return this.getRangeMs(this.value, "week");
    },

    dayRangeMs() {
      return this.getRangeMs(this.value, "day");
    },

    weekDays() {
      const { startMs } = this.weekRangeMs;
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(startMs + i * 86400000);
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        return {
          key: `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`,
          startMs: dayStart,
          endMs: dayStart + 86400000,
          dow: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()],
          dom: d.getDate(),
        };
      });
    },

    weekBodyHeightPx() {
      return `${this.totalMinutes * this.weekPxPerMin}px`;
    },

    unassignedLane() {
      const count =
        this.viewType === "week"
          ? this.calls.filter(
            (x) => this.isUnassigned(x.techId) && this.inRange(x.startMs, this.weekRangeMs)
          ).length
          : this.unassignedEventsForDay.length;

      return {
        id: null,
        name: "Unassigned",
        color: "#6B7280",
        avatarText: this.initials("Unassigned"),
        count,
        status: count > 0 ? "busy" : "available",
      };
    },

    unassignedEventsForDay() {
      const { startMs, endMs } = this.dayRangeMs;
      return this.calls.filter(
        (x) => this.isUnassigned(x.techId) && x.startMs >= startMs && x.startMs < endMs
      );
    },

    unassignedEventsForWeek() {
      const { startMs, endMs } = this.weekRangeMs;
      return this.calls
        .filter((x) => this.isUnassigned(x.techId) && x.startMs >= startMs && x.startMs < endMs)
        .sort((a, b) => a.startMs - b.startMs);
    },
  },

  mounted() {
    this.initialize();
  },

  methods: {
    async initialize() {
      await this.loadData();
      this.$nextTick(() => this.updateTitle());
    },

    async loadData() {
      await this.$store.dispatch("Data/getOpenCallData");
      await this.$store.dispatch("Data/getTechnianList");
      this.updateTitle();
    },

    async reloadCallsFromServer() {
      await this.$store.dispatch("Data/getOpenCallData");
    },

    showToast(text, color = "success") {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, text, color });
      setTimeout(() => this.removeToast(id), 6000);
    },

    removeToast(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx !== -1) this.toasts.splice(idx, 1);
    },

    extractImporterError(parsed) {
      try {
        const rows = parsed?.Data?.Tables?.[0]?.Rows ?? [];
        const failed = rows.find((r) => !r.IsSuccess && r.ImportError?.Message);
        if (!failed) return null;
        const inner = JSON.parse(failed.ImportError.Message);
        return inner?.ErrorMessage ?? failed.ImportError.Message;
      } catch {
        return null;
      }
    },

    makeImporterCallbacks(successMsg, failurePrefix) {
      return {
        onSuccess: () => this.showToast(successMsg, "success"),
        onFailure: (parsed) => {
          const detail = this.extractImporterError(parsed) ?? "Unknown error";
          this.showToast(`${failurePrefix}: ${detail}`, "error");
        },
      };
    },

    baseDayStartMs(dateLike) {
      if (!dateLike) return NaN;
      const d0 = new Date(dateLike);
      if (Number.isNaN(d0.getTime())) return NaN;
      return new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()).getTime();
    },

    combineDateAndSecondsToMs(dateLike, secondsFromMidnight) {
      const base = this.baseDayStartMs(dateLike);
      if (Number.isNaN(base)) return NaN;
      return base + Number(secondsFromMidnight ?? 0) * 1000;
    },

    getRangeMs(valueDateStr, type) {
      const base = new Date(`${valueDateStr}T00:00:00`);
      const start = new Date(base);
      const end = new Date(base);

      if (type === "week") {
        const day = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - day);
        end.setDate(start.getDate() + 7);
      } else {
        end.setDate(start.getDate() + 1);
      }

      return { startMs: start.getTime(), endMs: end.getTime() };
    },

    inRange(ms, range) {
      return ms >= range.startMs && ms < range.endMs;
    },

    isUnassigned(techId) {
      return !techId || !this.empIdSet.has(String(techId));
    },

    updateTitle() {
      if (this.viewType === "month" && this.$refs.cal?.title) {
        this.title = this.$refs.cal.title;
        return;
      }
      this.title = this.value;
    },

    onViewChange() {
      this.updateTitle();
    },

    prev() {
      if (this.viewType === "month" && this.$refs.cal) this.$refs.cal.prev();
      else this.shiftRange(-1);
      this.updateTitle();
    },

    next() {
      if (this.viewType === "month" && this.$refs.cal) this.$refs.cal.next();
      else this.shiftRange(1);
      this.updateTitle();
    },

    setToday() {
      this.value = formatDateLocal(new Date());
      this.updateTitle();
    },

    shiftRange(step) {
      const d = new Date(`${this.value}T00:00:00`);
      d.setDate(d.getDate() + step * (this.viewType === "week" ? 7 : 1));
      this.value = formatDateLocal(d);
    },

    onHeaderScroll() {
      if (this.syncFromRows) return;
      const h = this.$refs.timeHeader;
      const r = this.$refs.rows;
      if (!h || !r) return;
      this.syncFromHeader = true;
      r.scrollLeft = h.scrollLeft;
      this.syncFromHeader = false;
    },

    onRowsScroll() {
      if (this.syncFromHeader) return;
      const h = this.$refs.timeHeader;
      const r = this.$refs.rows;
      if (!h || !r) return;
      this.syncFromRows = true;
      h.scrollLeft = r.scrollLeft;
      this.syncFromRows = false;
    },

    onWeekScroll() { },

    eventsByTechForDay(techId) {
      const id = techId != null ? String(techId) : null;
      const { startMs, endMs } = this.dayRangeMs;
      return this.calls.filter(
        (x) => x.techId === id && x.startMs >= startMs && x.startMs < endMs
      );
    },

    eventsForWeekDay(dayStartMs, dayEndMs) {
      const { startMs, endMs } = this.weekRangeMs;
      return this.calls.filter((x) => {
        const inWeek = x.startMs >= startMs && x.startMs < endMs;
        const inDay = x.startMs >= dayStartMs && x.startMs < dayEndMs;
        const allowTech =
          !x.techId ||
          this.selectedTechIds.size === 0 ||
          this.selectedTechIds.has(String(x.techId));

        return inWeek && inDay && allowTech;
      });
    },

    techNameById(techId) {
      if (!techId) return "Unassigned";
      return this.techList.find((x) => x.id === String(techId))?.name || "Unassigned";
    },

    techLane(t) {
      const id = String(t.id);
      const count =
        this.viewType === "week"
          ? this.calls.filter((x) => x.techId === id && this.inRange(x.startMs, this.weekRangeMs)).length
          : this.eventsByTechForDay(id).length;

      return {
        id,
        name: t.name,
        color: t.color,
        avatarText: t.avatarText,
        count,
        status: count > 0 ? "busy" : "available",
      };
    },

    toggleTechFilter(techId) {
      const id = String(techId);
      if (this.selectedTechIds.has(id)) this.selectedTechIds.delete(id);
      else this.selectedTechIds.add(id);
      this.selectedTechIds = new Set(this.selectedTechIds);
    },

    setDragPayload(e, payloadObj) {
      const dt = e?.dataTransfer;
      if (!dt) return;
      const payload = JSON.stringify(payloadObj);
      dt.setData(DND_MIME, payload);
      dt.setData("text", payload);
      dt.effectAllowed = "move";
      dt.dropEffect = "move";
    },

    getDragPayload(e) {
      const dt = e?.dataTransfer;
      if (!dt) return null;
      const raw = dt.getData(DND_MIME) || dt.getData("text") || dt.getData("application/json");
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },

    onDragEnterDropZone(key, e) {
      this.dropTargetKey = key;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragOverDropZone(e) {
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragStart(ev, e) {
      this.dragEventId = ev.id;
      this.setDragPayload(e, {
        id: ev.id,
        originalId: ev.originalId,
        callNum: ev.callNum,
        oldTechId: ev.techId ?? null,
        seqNum: ev.seqNum ?? 1,
        jobNum: ev.jobNum,
      });
    },

    clearDrag() {
      this.dragEventId = null;
      this.dropTargetKey = null;
    },

    setPendingEventOverride(eventKey, patch) {
      this.$set(this.pendingEventOverrides, eventKey, {
        ...(this.pendingEventOverrides[eventKey] || {}),
        ...patch,
      });
    },

    clearPendingEventOverride(eventKey) {
      this.$delete(this.pendingEventOverrides, eventKey);
    },

    async persistReassign(callNum, oldTechId, newTechId, seqNum) {
      const empSet = this.empIdSet;

      if (oldTechId && empSet.has(String(oldTechId))) {
        await this.$store.dispatch("Data/importer", {
          tableName: "UnpickJob",
          importerName: "RemoveTech",
          rows: [
            {
              Company: COMPANY,
              CallNum: Number(callNum),
              EmpID: String(oldTechId),
              SeqNum: Number(seqNum),
            },
          ],
          flags: {
            delete: true,
            update: false,
            add: false,
            rollbackWhenFailed: false,
          },
          ...this.makeImporterCallbacks(
            `Remove Tech successfully #${callNum}`,
            `Remove Tech error #${callNum}`
          ),
        });
      }

      if (newTechId) {
        await this.$store.dispatch("Data/importer", {
          tableName: "CallTech",
          importerName: "CallTech",
          rows: [
            {
              Company: COMPANY,
              CallNum: Number(callNum),
              EmpID: String(newTechId),
              SeqNum: Number(seqNum),
            },
          ],
          flags: {
            add: true,
            update: false,
            delete: false,
            rollbackWhenFailed: false,
          },
          ...this.makeImporterCallbacks(
            `Assign Tech successfully #${callNum}`,
            `Assign Tech error #${callNum}`
          ),
        });
      }
    },

    async handleDropTech(targetTechId, e) {
      const drag = this.getDragPayload(e);
      if (!drag) return;

      const eventId = drag.id;
      const oldTechId = drag.oldTechId != null ? String(drag.oldTechId) : null;
      const newTechId = targetTechId != null ? String(targetTechId) : null;
      const seqNum = Number(drag.seqNum ?? 1);

      if (oldTechId === newTechId) {
        this.clearDrag();
        return;
      }

      const oldEvent = this.calls.find((x) => x.id === eventId || x.originalId === drag.originalId);
      if (!oldEvent) {
        this.clearDrag();
        return;
      }

      const oldEventKey = oldEvent.originalId || oldEvent.id;

      try {
        this.setPendingEventOverride(oldEventKey, { techId: newTechId });
        await this.persistReassign(oldEvent.callNum, oldTechId, newTechId, seqNum);

        if (oldEvent.jobNum) {
          await this.reschedule(oldEvent.jobNum);
        }
        await new Promise((r) => setTimeout(r, 300));
        await this.reloadCallsFromServer();
        this.clearPendingEventOverride(oldEventKey);
        this.showToast(`Reassigned successfully #${oldEvent.callNum}`, "success");
      } catch (err) {
        this.clearPendingEventOverride(oldEventKey);
        console.error("handleDropTech error", err);
        throw err;
      } finally {
        this.clearDrag();
      }
    },

    async reschedule(jobNum) {
      const dsScheduleEngine = [
        {
          Company: COMPANY,
          JobNum: jobNum,
          AssemblySeq: 0,
          OprSeq: 0,
          StartDate: new Date(),
          StartTime: 0,
          WhatIf: false,
          Finite: false,
          SchedTypeCode: "JJ",
          ScheduleDirection: "Start",
          SetupComplete: false,
          ProductionComplete: false,
          OverrideMtlCon: false,
          OverRideHistDateSetting: 2,
          RecalcExpProdYld: false,
          UseSchedulingMultiJob: false,
          SchedulingMultiJobIgnoreLocks: false,
          SchedulingMultiJobMinimizeWIP: false,
          RowMod: "A",
        },
      ];

      await this.$store.dispatch("Data/callBO", {
        bo: "Erp.BO.ScheduleEngine",
        method: "MoveJobItem",
        body: {
          ds: {
            ScheduleEngine: dsScheduleEngine,
          },
        },
        bpmData: [],
      });
    },

    eventWidthPx(ev) {
      const leftMin = this.toTimelineMin(ev.startMs);
      const rightMin = this.toTimelineMin(ev.endMs);
      const widthMin = Math.max(15, rightMin - leftMin);
      return widthMin * this.pxPerMin;
    },

    isTinyEvent(ev) {
      return this.eventWidthPx(ev) < this.tinyEventWidthPx;
    },

    eventClasses(ev) {
      return { "is-tiny": this.isTinyEvent(ev) };
    },

    pad2(n) {
      return String(n).padStart(2, "0");
    },

    clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    },

    msToOffsetMin(ms) {
      const d = new Date(ms);
      return d.getHours() * 60 + d.getMinutes() - this.dayStartHour * 60;
    },

    toTimelineMin(ms) {
      return this.clamp(this.msToOffsetMin(ms), 0, this.totalMinutes);
    },

    minutesToPx(min) {
      return `${this.laneGutterPx + min * this.pxPerMin}px`;
    },

    hourToPx(hour) {
      return this.minutesToPx((hour - this.dayStartHour) * 60);
    },

    formatHM(ms) {
      const d = new Date(ms);
      return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())}`;
    },

    weekMinToTop(min) {
      return `${min * this.weekPxPerMin}px`;
    },

    toDayMinute(ms) {
      const d = new Date(ms);
      return d.getHours() * 60 + d.getMinutes();
    },

    eventStyleWeek(ev) {
      const main = ev.color || "#0C969C";
      const bg = this.hexToRgba(main, 0.18);
      const startMin = this.toDayMinute(ev.startMs);
      const endMin = this.toDayMinute(ev.endMs);
      const height = Math.max(18, (endMin - startMin) * this.weekPxPerMin);

      return {
        top: `${startMin * this.weekPxPerMin}px`,
        height: `${height}px`,
        background: bg,
        borderLeft: `4px solid ${main}`,
        color: "#053238",
      };
    },

    eventStyleDay(ev) {
      const main = ev.color || "#0C969C";
      const bg = this.hexToRgba(main, 0.18);
      const leftMin = this.toTimelineMin(ev.startMs);
      const rightMin = this.toTimelineMin(ev.endMs);
      const widthMin = Math.max(15, rightMin - leftMin);

      return {
        left: `${this.laneGutterPx + leftMin * this.pxPerMin}px`,
        width: `${widthMin * this.pxPerMin}px`,
        top: "50%",
        transform: "translateY(-50%)",
        height: "40px",
        background: bg,
        color: "#053238",
        borderLeft: `4px solid ${main}`,
        borderRadius: "10px",
        boxSizing: "border-box",
      };
    },

    statusColor(status) {
      if (status === "busy") return "#F78C6A";
      if (status === "available") return "#06D7A0";
      return "#9e9e9e";
    },

    laneChipStyle(lane) {
      const main = lane.color || "#6B7280";
      return {
        background: this.hexToRgba(main, 0.14),
        color: "#0b1f23",
        border: "none",
      };
    },

    initials(name) {
      const parts = String(name || "").trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    hexToRgba(hex, alpha = 0.15) {
      const h = String(hex || "").replace("#", "");
      if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
      const bigint = parseInt(h, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    stringHash(str) {
      return [...String(str)].reduce((a, c) => a + c.charCodeAt(0), 0);
    },

    techColor(key) {
      return TECH_PALETTE[this.stringHash(key) % TECH_PALETTE.length];
    },

    onDragEnterWeekDay(dayIdx, e) {
      this.dropTargetKey = `week-day-${dayIdx}`;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragOverWeekDay(dayIdx, e) {
      this.dropTargetKey = `week-day-${dayIdx}`;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    roundToStep(min, step = 15) {
      return Math.round(min / step) * step;
    },

    getMinutesFromWeekDropEvent(dayEl, e) {
      const rect = dayEl.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const rawMin = y / this.weekPxPerMin;
      return this.clamp(this.roundToStep(rawMin, 15), 0, 24 * 60);
    },

    async handleDropWeekDay(day, dayIdx, e) {
      const drag = this.getDragPayload(e);
      if (!drag) return;

      const oldEvent = this.calls.find((x) => x.id === drag.id || x.originalId === drag.originalId);
      if (!oldEvent) {
        this.clearDrag();
        return;
      }

      const durationMs = oldEvent.endMs - oldEvent.startMs;
      const newStartMs = day.startMs + this.getMinutesFromWeekDropEvent(e.currentTarget, e) * 60 * 1000;
      const newEndMs = newStartMs + durationMs;
      const eventKey = oldEvent.originalId || oldEvent.id;

      try {
        this.setPendingEventOverride(eventKey, { startMs: newStartMs, endMs: newEndMs });
        await this.persistMoveSchedule({ call: oldEvent, newStartMs });
        await new Promise((r) => setTimeout(r, 300));
        await this.reloadCallsFromServer();
        this.clearPendingEventOverride(eventKey);
      } catch (err) {
        this.clearPendingEventOverride(eventKey);
        throw err;
      } finally {
        this.clearDrag();
      }
    },

    async persistMoveSchedule({ call, newStartMs }) {
      const callNum = Number(call.callNum || call.CallNum);
      if (!callNum) throw new Error("Invalid CallNum");

      const start = new Date(newStartMs);
      const schedDate = formatDateLocal(start);
      const schedTime = toSecondsFromMidnight(start);

      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Erp.BO.ServiceCallCenter",
        method: "GetByID",
        body: { callNum },
      });

      const ds = JSON.parse(JSON.stringify(getResp?.returnObj || {}));

      if (!ds?.FSCallhd?.length) {
        throw new Error(`Service call not found: ${callNum}`);
      }

      const head = ds.FSCallhd[0];
      head.SchedDate = `${schedDate}T00:00:00`;
      head.SchedTime = schedTime;
      head.RowMod = "U";

      const updateResp = await this.$store.dispatch("Data/callBO", {
        bo: "Erp.BO.ServiceCallCenter",
        method: "Update",
        body: { ds },
      });

      if (updateResp?.error) {
        throw new Error(updateResp.error);
      }

      if (call.jobNum) {
        await this.reschedule(call.jobNum);
        this.showToast(`Schedule + reschedule successfully #${callNum}`, "success");
      } else {
        this.showToast(`Schedule updated successfully #${callNum}`, "success");
      }

      return updateResp;
    },

    openEventDialog(ev) {
      this.selectedEvent = ev;
      this.dialog = true;
    },
  },
};
</script>

<style scoped>
.fs-scheduler {
  width: 100%;
}

.lanes-wrapper {
  border-left: 1px solid #eee;
}

.time-header {
  height: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #eee;
}

.time-header-inner {
  position: relative;
  height: 48px;
}

.time-tick {
  position: absolute;
  top: 14px;
  transform: translateX(-50%);
  font-size: 12px;
  color: #666;
}

.grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #f0f0f0;
}

.rows {
  height: calc(100vh - 168px);
  overflow: auto;
}

.lane {
  position: relative;
  display: block;
}

.lane-title {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  pointer-events: none;
}

.lane-title .v-chip {
  pointer-events: auto;
}

.row-inner {
  position: relative;
  height: 64px;
  background: #fff;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.row-inner.is-drop-target {
  box-shadow: inset 0 0 0 2px #0c969c;
  background: rgba(12, 150, 156, 0.05);
}

.drop-label {
  position: sticky;
  left: 248px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin-top: 18px;
  padding: 0 10px 0 4px;
  border-radius: 14px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
}

.drop-label__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.toast-stack {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  pointer-events: none;
}

.toast-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 280px;
  max-width: 420px;
  padding: 12px 16px;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
  pointer-events: auto;
  margin: 5px;
}

.toast--success {
  background: #4caf50;
}

.toast--error {
  background: #f44336;
}

.toast--info {
  background: #2196f3;
}

.toast--warning {
  background: #ff9800;
}

.toast-item__text {
  flex: 1;
  line-height: 1.4;
}

.toast-item__close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  cursor: pointer;
  padding: 0 0 0 4px;
  flex-shrink: 0;
}

.toast-item__close:hover {
  color: #fff;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(40px);
}

.row-inner::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: #f0f0f0;
  pointer-events: none;
  z-index: 1;
}

.lane-chip {
  border-radius: 999px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px 0 6px;
}

.lane-chip__name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lane-chip__count {
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}

.event-block {
  position: absolute;
  z-index: 2;
  border-radius: 10px;
  padding: 6px 10px;
  cursor: grab;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  overflow: hidden;
}

.event-title {
  font-size: 12px;
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-sub {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-block.is-tiny {
  padding: 0 8px;
  align-items: center;
  text-align: center;
}

.event-num {
  font-weight: 900;
}

.week-wrapper {
  display: grid;
  grid-template-columns: 260px 1fr;
  height: calc(100vh - 120px);
  border-top: 1px solid #eee;
}

.tech-panel {
  border-right: 1px solid #eee;
  padding: 12px;
  overflow: auto;
}

.tech-panel__title {
  font-weight: 800;
  font-size: 16px;
  margin-bottom: 10px;
}

.tech-item {
  margin-bottom: 20px;
  border-radius: 8px;
  padding: 4px;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.tech-item.is-drop-target {
  box-shadow: 0 0 0 2px #0c969c;
  background: rgba(12, 150, 156, 0.05);
}

.tech-panel__hint {
  font-size: 12px;
  opacity: 0.7;
  margin-top: 12px;
}

.lane-chip.is-selected {
  outline: 2px solid rgba(12, 150, 156, 0.35);
}

.unassigned-list {
  margin: 10px 0 16px;
  display: grid;
  gap: 8px;
}

.unassigned-item {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: grab;
  background: #fff;
}

.unassigned-item__title {
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unassigned-item__sub {
  font-size: 11px;
  opacity: 0.75;
  margin-top: 2px;
}

.week-grid {
  display: grid;
  grid-template-rows: 56px 1fr;
  min-width: 880px;
}

.week-grid__header {
  display: grid;
  grid-template-columns: 64px 1fr;
  border-bottom: 1px solid #eee;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 5;
}

.week-grid__corner {
  border-right: 1px solid #eee;
}

.week-grid__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.day-col-head {
  padding: 8px 10px;
  border-right: 1px solid #eee;
}

.day-col-head:last-child {
  border-right: none;
}

.dow {
  font-size: 12px;
  opacity: 0.75;
}

.dom {
  font-size: 18px;
  font-weight: 800;
  margin-top: 2px;
}

.week-grid__body {
  display: grid;
  grid-template-columns: 64px 1fr;
  overflow: auto;
  position: relative;
}

.time-col {
  position: relative;
  border-right: 1px solid #eee;
  background: #fff;
}

.time-label {
  position: absolute;
  left: 8px;
  transform: translateY(-50%);
  font-size: 11px;
  color: #777;
}

.days-area {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  position: relative;
  background: #fff;
}

.day-col {
  position: relative;
  border-right: 1px solid #eee;
}

.day-col:last-child {
  border-right: none;
}

.hour-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #f0f0f0;
}

.week-event {
  position: absolute;
  left: 6px;
  right: 6px;
  border-radius: 10px;
  padding: 6px 8px;
  box-sizing: border-box;
  cursor: grab;
  overflow: hidden;
}

.week-event__title {
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-event__sub {
  font-size: 11px;
  opacity: 0.8;
}

.day-col.is-drop-target {
  background: rgba(12, 150, 156, 0.06);
  box-shadow: inset 0 0 0 2px #0c969c;
}
</style>
